/**
 * Iter-235c — AI Event Insights tile + page.
 *
 * Tile: a compact card on the admin dashboard showing recap volume,
 * voice-vs-text mix, avg rating, top recurring highlights/concerns
 * for the last 30 days. Links into the full /admin/ai-insights page.
 *
 * Page: the same data with a 30/60/90-day selector and a deep table
 * of top phrases — gives ops a single place to spot recurring issues
 * (e.g. "parking confusion" appearing 8 times in 30 days).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Mic, Type, Star, TrendingUp, AlertTriangle, Loader2, ArrowRight } from 'lucide-react';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

export function AIInsightsTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/ai-insights/summary?days=30');
        setData(r.data);
      } catch (_) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Card data-testid="ai-insights-tile" className="border-cyan-100">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-cyan-600" />
          <h3 className="text-sm font-semibold text-slate-900">AI Event Insights</h3>
          <span className="ml-auto text-[10px] text-slate-400">last 30 days</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-6 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : !data || data.recap_total === 0 ? (
          <div className="text-xs text-slate-500 py-2">
            No confirmed recaps yet — voice + typed recaps will appear here once talent submit them.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Stat label="Recaps" value={data.recap_total} icon={Sparkles} />
              <Stat label="Avg rating" value={data.avg_rating ?? '—'} icon={Star} valueClass="text-amber-600" />
              <Stat label="Voice share" value={`${data.voice_share_pct}%`} icon={Mic} valueClass="text-cyan-600" />
            </div>
            <div className="text-[11px] text-slate-500 mb-1">Recap completion: <b className="text-slate-700">{data.completion_pct ?? 0}%</b> of {data.shifts_ended ?? 0} ended shifts</div>
            <PhraseRow label="Top highlight" icon={TrendingUp} tone="emerald" items={data.top_highlights} />
            <PhraseRow label="Top concern" icon={AlertTriangle} tone="rose" items={data.top_concerns} />
          </>
        )}

        <div className="mt-3 pt-2 border-t border-slate-100">
          <Link to="/admin/ai-insights">
            <Button variant="ghost" size="sm" className="w-full justify-between text-xs text-cyan-700" data-testid="ai-insights-open">
              Open AI Insights <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, icon: Icon, valueClass = '' }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2">
      <div className="flex items-center gap-1 text-[10px] uppercase text-slate-500 font-semibold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-base font-bold mt-0.5 ${valueClass}`}>{value}</div>
    </div>
  );
}

function PhraseRow({ label, icon: Icon, tone, items }) {
  if (!items?.length) return null;
  const cls = tone === 'emerald'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';
  return (
    <div className="mt-2">
      <div className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1 mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="flex flex-wrap gap-1">
        {items.slice(0, 4).map((p) => (
          <span key={p.phrase} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] ${cls}`}>
            {p.phrase} <span className="opacity-70">×{p.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AIInsightsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/admin/ai-insights/summary?days=${days}`);
        if (!cancelled) setData(r.data);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="ai-insights-page">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-6 h-6 text-cyan-600" />
        <h1 className="text-2xl font-bold">AI Event Insights</h1>
        <span className="ml-auto inline-flex border rounded-lg overflow-hidden">
          {[30, 60, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    className={`px-3 py-1 text-xs font-semibold ${days === d ? 'bg-cyan-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                    data-testid={`ai-insights-window-${d}`}>
              {d}d
            </button>
          ))}
        </span>
      </div>

      {loading ? (
        <div className="text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : !data ? (
        <p className="text-slate-500">Couldn&apos;t load insights right now.</p>
      ) : data.recap_total === 0 ? (
        <Card><CardContent className="p-6 text-slate-500 text-center">
          No recaps submitted in the last {days} days yet. Voice + typed recaps will appear here once talent submit them after shifts.
        </CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Stat label="Recaps" value={data.recap_total} icon={Sparkles} />
            <Stat label="Voice" value={data.voice_count} icon={Mic} valueClass="text-cyan-600" />
            <Stat label="Typed" value={data.text_count} icon={Type} />
            <Stat label="Avg rating" value={data.avg_rating ?? '—'} icon={Star} valueClass="text-amber-600" />
            <Stat label="Completion" value={`${data.completion_pct ?? 0}%`} icon={TrendingUp} valueClass="text-emerald-600" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <Card><CardContent className="p-4">
              <div className="text-xs font-semibold text-emerald-700 flex items-center gap-1 mb-2">
                <TrendingUp className="w-3.5 h-3.5" /> Recurring highlights
              </div>
              <PhraseList items={data.top_highlights} tone="emerald" testIdPrefix="insight-highlight" />
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs font-semibold text-rose-700 flex items-center gap-1 mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Recurring concerns
              </div>
              <PhraseList items={data.top_concerns} tone="rose" testIdPrefix="insight-concern" />
            </CardContent></Card>
          </div>

          <Card><CardContent className="p-4">
            <div className="text-xs font-semibold text-slate-700 mb-2">Rating distribution</div>
            <div className="space-y-1">
              {[5, 4, 3, 2, 1].map((n) => {
                const ct = data.rating_distribution[String(n)] || 0;
                const pct = data.recap_total ? Math.round((ct / data.recap_total) * 100) : 0;
                return (
                  <div key={n} className="flex items-center gap-2 text-xs">
                    <span className="w-4 font-semibold">{n}★</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${n >= 4 ? 'bg-emerald-500' : n === 3 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-12 text-right text-slate-500">{ct} · {pct}%</span>
                  </div>
                );
              })}
            </div>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}

function PhraseList({ items, tone, testIdPrefix }) {
  if (!items?.length) return <p className="text-xs text-slate-400 italic">Nothing recurring yet — talent need more recaps.</p>;
  const cls = tone === 'emerald'
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : 'bg-rose-50 text-rose-800 border-rose-200';
  return (
    <ul className="space-y-1">
      {items.map((p, i) => (
        <li key={p.phrase} className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border text-xs ${cls}`} data-testid={`${testIdPrefix}-${i}`}>
          <span className="truncate">{p.phrase}</span>
          <span className="text-[10px] opacity-70 ml-2">×{p.count}</span>
        </li>
      ))}
    </ul>
  );
}
